import type { NamespaceCatalog } from '@/i18n/ns';

// The chat review card for suggest_page_edits (components/assistant-ui). It
// names the page a suggestion changes and says that approving only sends a
// suggestion: the page changes when a publisher accepts it in Knowledge.
// English and Swedish are source copy; the other languages are drafts for
// human review.
const en = {
  loading: 'Finding the page…',
  unavailable: 'an unavailable page',
  unavailableTitle: 'Unavailable page',
  untitled: 'Untitled page',
  create: 'New page “{title}” under {parent}',
  move: 'Move {page} under {parent}',
  archive: 'Archive {page}',
  rename: 'Rename {page} to “{title}”',
  send: 'Send suggestion',
  pendingFooter:
    'Approving sends this as a suggestion. The page changes only when a publisher accepts it.',
};
type Key = keyof typeof en;
const copy: NamespaceCatalog<Key> = {
  en,
  bg: {
    loading: 'Търсене на страницата…',
    unavailable: 'недостъпна страница',
    unavailableTitle: 'Недостъпна страница',
    untitled: 'Страница без заглавие',
    create: 'Нова страница „{title}“ под {parent}',
    move: 'Премести {page} под {parent}',
    archive: 'Архивирай {page}',
    rename: 'Преименувай {page} на „{title}“',
    send: 'Изпрати предложение',
    pendingFooter:
      'С одобрението това се изпраща като предложение. Страницата се променя едва когато някой с право да публикува го приеме.',
  },
  cs: {
    loading: 'Hledání stránky…',
    unavailable: 'nedostupná stránka',
    unavailableTitle: 'Nedostupná stránka',
    untitled: 'Stránka bez názvu',
    create: 'Nová stránka „{title}“ pod stránkou {parent}',
    move: 'Přesunout {page} pod stránku {parent}',
    archive: 'Archivovat {page}',
    rename: 'Přejmenovat {page} na „{title}“',
    send: 'Odeslat návrh',
    pendingFooter:
      'Schválením se to odešle jako návrh. Stránka se změní, až když ho přijme někdo s právem publikovat.',
  },
  da: {
    loading: 'Finder siden…',
    unavailable: 'en utilgængelig side',
    unavailableTitle: 'Utilgængelig side',
    untitled: 'Side uden titel',
    create: 'Ny side »{title}« under {parent}',
    move: 'Flyt {page} under {parent}',
    archive: 'Arkivér {page}',
    rename: 'Omdøb {page} til »{title}«',
    send: 'Send forslag',
    pendingFooter:
      'Når du godkender, sendes dette som et forslag. Siden ændres først, når en med ret til at udgive accepterer det.',
  },
  de: {
    loading: 'Seite wird gesucht…',
    unavailable: 'eine nicht verfügbare Seite',
    unavailableTitle: 'Nicht verfügbare Seite',
    untitled: 'Seite ohne Titel',
    create: 'Neue Seite „{title}“ unter {parent}',
    move: '{page} unter {parent} verschieben',
    archive: '{page} archivieren',
    rename: '{page} in „{title}“ umbenennen',
    send: 'Vorschlag senden',
    pendingFooter:
      'Mit der Freigabe wird dies als Vorschlag gesendet. Die Seite ändert sich erst, wenn jemand mit Veröffentlichungsrechten ihn annimmt.',
  },
  el: {
    loading: 'Αναζήτηση σελίδας…',
    unavailable: 'μια μη διαθέσιμη σελίδα',
    unavailableTitle: 'Μη διαθέσιμη σελίδα',
    untitled: 'Σελίδα χωρίς τίτλο',
    create: 'Νέα σελίδα «{title}» κάτω από {parent}',
    move: 'Μετακίνηση {page} κάτω από {parent}',
    archive: 'Αρχειοθέτηση {page}',
    rename: 'Μετονομασία {page} σε «{title}»',
    send: 'Αποστολή πρότασης',
    pendingFooter:
      'Με την έγκριση, αυτό αποστέλλεται ως πρόταση. Η σελίδα αλλάζει μόνο όταν την αποδεχτεί κάποιος με δικαίωμα δημοσίευσης.',
  },
  es: {
    loading: 'Buscando la página…',
    unavailable: 'una página no disponible',
    unavailableTitle: 'Página no disponible',
    untitled: 'Página sin título',
    create: 'Nueva página «{title}» dentro de {parent}',
    move: 'Mover {page} dentro de {parent}',
    archive: 'Archivar {page}',
    rename: 'Cambiar el nombre de {page} a «{title}»',
    send: 'Enviar sugerencia',
    pendingFooter:
      'Al aprobar, esto se envía como sugerencia. La página solo cambia cuando alguien con permiso para publicar la acepta.',
  },
  et: {
    loading: 'Lehe otsimine…',
    unavailable: 'kättesaamatu leht',
    unavailableTitle: 'Kättesaamatu leht',
    untitled: 'Pealkirjata leht',
    create: 'Uus leht „{title}” lehe {parent} alla',
    move: 'Teisalda {page} lehe {parent} alla',
    archive: 'Arhiveeri {page}',
    rename: 'Nimeta {page} ümber: „{title}”',
    send: 'Saada ettepanek',
    pendingFooter:
      'Kinnitamisel saadetakse see ettepanekuna. Leht muutub alles siis, kui avaldamisõigusega inimene selle vastu võtab.',
  },
  fi: {
    loading: 'Haetaan sivua…',
    unavailable: 'sivu, joka ei ole saatavilla',
    unavailableTitle: 'Sivu ei ole saatavilla',
    untitled: 'Nimetön sivu',
    create: 'Uusi sivu ”{title}” sivun {parent} alle',
    move: 'Siirrä {page} sivun {parent} alle',
    archive: 'Arkistoi {page}',
    rename: 'Nimeä {page} uudelleen: ”{title}”',
    send: 'Lähetä ehdotus',
    pendingFooter:
      'Hyväksyminen lähettää tämän ehdotuksena. Sivu muuttuu vasta, kun julkaisuoikeuden omaava henkilö hyväksyy sen.',
  },
  fr: {
    loading: 'Recherche de la page…',
    unavailable: 'une page indisponible',
    unavailableTitle: 'Page indisponible',
    untitled: 'Page sans titre',
    create: 'Nouvelle page « {title} » sous {parent}',
    move: 'Déplacer {page} sous {parent}',
    archive: 'Archiver {page}',
    rename: 'Renommer {page} en « {title} »',
    send: 'Envoyer la suggestion',
    pendingFooter:
      'En approuvant, vous envoyez ceci comme suggestion. La page ne change que lorsqu’une personne autorisée à publier l’accepte.',
  },
  ga: {
    loading: 'An leathanach á aimsiú…',
    unavailable: 'leathanach nach bhfuil ar fáil',
    unavailableTitle: 'Leathanach nach bhfuil ar fáil',
    untitled: 'Leathanach gan teideal',
    create: 'Leathanach nua “{title}” faoi {parent}',
    move: 'Bog {page} faoi {parent}',
    archive: 'Cartlannaigh {page}',
    rename: 'Athainmnigh {page} mar “{title}”',
    send: 'Seol moladh',
    pendingFooter:
      'Nuair a fhaomhann tú é, seoltar é seo mar mholadh. Ní athraíonn an leathanach go dtí go nglacann duine a bhfuil cead foilsithe aige leis.',
  },
  hr: {
    loading: 'Traženje stranice…',
    unavailable: 'nedostupna stranica',
    unavailableTitle: 'Nedostupna stranica',
    untitled: 'Stranica bez naslova',
    create: 'Nova stranica „{title}” ispod {parent}',
    move: 'Premjesti {page} ispod {parent}',
    archive: 'Arhiviraj {page}',
    rename: 'Preimenuj {page} u „{title}”',
    send: 'Pošalji prijedlog',
    pendingFooter:
      'Odobravanjem se ovo šalje kao prijedlog. Stranica se mijenja tek kad ga prihvati netko s pravom objave.',
  },
  hu: {
    loading: 'Az oldal keresése…',
    unavailable: 'egy nem elérhető oldal',
    unavailableTitle: 'Nem elérhető oldal',
    untitled: 'Cím nélküli oldal',
    create: 'Új oldal „{title}” címmel ide: {parent}',
    move: '{page} áthelyezése ide: {parent}',
    archive: '{page} archiválása',
    rename: '{page} átnevezése erre: „{title}”',
    send: 'Javaslat küldése',
    pendingFooter:
      'A jóváhagyás ezt javaslatként küldi el. Az oldal csak akkor változik, ha egy közzétételi joggal rendelkező személy elfogadja.',
  },
  it: {
    loading: 'Ricerca della pagina…',
    unavailable: 'una pagina non disponibile',
    unavailableTitle: 'Pagina non disponibile',
    untitled: 'Pagina senza titolo',
    create: 'Nuova pagina «{title}» sotto {parent}',
    move: 'Sposta {page} sotto {parent}',
    archive: 'Archivia {page}',
    rename: 'Rinomina {page} in «{title}»',
    send: 'Invia suggerimento',
    pendingFooter:
      'Approvando, invii questo come suggerimento. La pagina cambia solo quando qualcuno con diritti di pubblicazione lo accetta.',
  },
  lt: {
    loading: 'Ieškomas puslapis…',
    unavailable: 'nepasiekiamas puslapis',
    unavailableTitle: 'Nepasiekiamas puslapis',
    untitled: 'Puslapis be pavadinimo',
    create: 'Naujas puslapis „{title}“ po {parent}',
    move: 'Perkelti {page} po {parent}',
    archive: 'Archyvuoti {page}',
    rename: 'Pervadinti {page} į „{title}“',
    send: 'Siųsti pasiūlymą',
    pendingFooter:
      'Patvirtinus tai siunčiama kaip pasiūlymas. Puslapis pasikeis tik tada, kai jį priims asmuo, turintis teisę skelbti.',
  },
  lv: {
    loading: 'Meklē lapu…',
    unavailable: 'nepieejama lapa',
    unavailableTitle: 'Nepieejama lapa',
    untitled: 'Lapa bez nosaukuma',
    create: 'Jauna lapa “{title}” zem {parent}',
    move: 'Pārvietot {page} zem {parent}',
    archive: 'Arhivēt {page}',
    rename: 'Pārdēvēt {page} par “{title}”',
    send: 'Nosūtīt ieteikumu',
    pendingFooter:
      'Apstiprinot tas tiek nosūtīts kā ieteikums. Lapa mainās tikai tad, kad to pieņem kāds ar publicēšanas tiesībām.',
  },
  mt: {
    loading: 'Qed tinstab il-paġna…',
    unavailable: 'paġna mhux disponibbli',
    unavailableTitle: 'Paġna mhux disponibbli',
    untitled: 'Paġna mingħajr titlu',
    create: 'Paġna ġdida “{title}” taħt {parent}',
    move: 'Mexxi {page} taħt {parent}',
    archive: 'Arkivja {page}',
    rename: 'Ibdel l-isem ta’ {page} għal “{title}”',
    send: 'Ibgħat suġġeriment',
    pendingFooter:
      'Meta tapprova, dan jintbagħat bħala suġġeriment. Il-paġna tinbidel biss meta jaċċettah xi ħadd bid-dritt li jippubblika.',
  },
  nl: {
    loading: 'Pagina zoeken…',
    unavailable: 'een niet-beschikbare pagina',
    unavailableTitle: 'Niet-beschikbare pagina',
    untitled: 'Pagina zonder titel',
    create: 'Nieuwe pagina “{title}” onder {parent}',
    move: '{page} verplaatsen naar onder {parent}',
    archive: '{page} archiveren',
    rename: '{page} hernoemen naar “{title}”',
    send: 'Suggestie versturen',
    pendingFooter:
      'Als je goedkeurt, wordt dit als suggestie verstuurd. De pagina verandert pas als iemand met publicatierechten het accepteert.',
  },
  pl: {
    loading: 'Szukanie strony…',
    unavailable: 'niedostępna strona',
    unavailableTitle: 'Niedostępna strona',
    untitled: 'Strona bez tytułu',
    create: 'Nowa strona „{title}” pod stroną {parent}',
    move: 'Przenieś {page} pod stronę {parent}',
    archive: 'Zarchiwizuj {page}',
    rename: 'Zmień nazwę {page} na „{title}”',
    send: 'Wyślij sugestię',
    pendingFooter:
      'Zatwierdzenie wysyła to jako sugestię. Strona zmieni się dopiero, gdy zaakceptuje ją osoba z prawem publikacji.',
  },
  pt: {
    loading: 'A procurar a página…',
    unavailable: 'uma página indisponível',
    unavailableTitle: 'Página indisponível',
    untitled: 'Página sem título',
    create: 'Nova página «{title}» em {parent}',
    move: 'Mover {page} para {parent}',
    archive: 'Arquivar {page}',
    rename: 'Mudar o nome de {page} para «{title}»',
    send: 'Enviar sugestão',
    pendingFooter:
      'Ao aprovar, isto é enviado como sugestão. A página só muda quando alguém com permissão para publicar a aceitar.',
  },
  ro: {
    loading: 'Se caută pagina…',
    unavailable: 'o pagină indisponibilă',
    unavailableTitle: 'Pagină indisponibilă',
    untitled: 'Pagină fără titlu',
    create: 'Pagină nouă „{title}” sub {parent}',
    move: 'Mută {page} sub {parent}',
    archive: 'Arhivează {page}',
    rename: 'Redenumește {page} în „{title}”',
    send: 'Trimite sugestia',
    pendingFooter:
      'Prin aprobare, aceasta este trimisă ca sugestie. Pagina se schimbă doar când o acceptă cineva cu drept de publicare.',
  },
  sk: {
    loading: 'Hľadá sa stránka…',
    unavailable: 'nedostupná stránka',
    unavailableTitle: 'Nedostupná stránka',
    untitled: 'Stránka bez názvu',
    create: 'Nová stránka „{title}“ pod stránkou {parent}',
    move: 'Presunúť {page} pod stránku {parent}',
    archive: 'Archivovať {page}',
    rename: 'Premenovať {page} na „{title}“',
    send: 'Odoslať návrh',
    pendingFooter:
      'Schválením sa to odošle ako návrh. Stránka sa zmení, až keď ho prijme niekto s právom publikovať.',
  },
  sl: {
    loading: 'Iskanje strani…',
    unavailable: 'nedosegljiva stran',
    unavailableTitle: 'Nedosegljiva stran',
    untitled: 'Stran brez naslova',
    create: 'Nova stran »{title}« pod {parent}',
    move: 'Premakni {page} pod {parent}',
    archive: 'Arhiviraj {page}',
    rename: 'Preimenuj {page} v »{title}«',
    send: 'Pošlji predlog',
    pendingFooter:
      'Z odobritvijo se to pošlje kot predlog. Stran se spremeni šele, ko ga sprejme nekdo s pravico do objave.',
  },
  sv: {
    loading: 'Hittar sidan…',
    unavailable: 'en sida som inte är tillgänglig',
    unavailableTitle: 'Sidan är inte tillgänglig',
    untitled: 'Sida utan titel',
    create: 'Ny sida ”{title}” under {parent}',
    move: 'Flytta {page} under {parent}',
    archive: 'Arkivera {page}',
    rename: 'Byt namn på {page} till ”{title}”',
    send: 'Skicka förslag',
    pendingFooter:
      'När du godkänner skickas detta som ett förslag. Sidan ändras först när någon med publiceringsrätt godkänner det.',
  },
};

export default copy;
